import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_URL,
  LOCALES,
  stripLocalePrefix,
  getLocaleFromPath,
  buildUrl,
  isNoIndexPath,
  type Locale,
} from "@/lib/seo";

type LocaleMeta = { title: string; description: string };

type PageMeta = { EN: LocaleMeta; PT: LocaleMeta; ES: LocaleMeta; image?: string };

const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const PAGE_META: Record<string, PageMeta> = {
  "/": {
    EN: {
      title: "Arandia Law Firm | Immigration & Criminal Defense",
      description:
        "Immigration attorneys in NY, NJ and FL, with criminal defense in New York. 25+ years protecting immigrant families. Trilingual EN/PT/ES.",
    },
    PT: {
      title: "Advogado de Imigração NY, NJ e FL | Criminal em NY",
      description:
        "Advogados de imigração em NY, NJ e FL e defesa criminal em Nova York. Mais de 25 anos protegendo famílias imigrantes. Atendimento trilíngue.",
    },
    ES: {
      title: "Abogado de Inmigración NY, NJ y FL | Penal en NY",
      description:
        "Abogados de inmigración en NY, NJ y FL y defensa penal en Nueva York. Más de 25 años protegiendo familias inmigrantes. Atención trilingüe.",
    },
  },
  "/contact": {
    EN: {
      title: "Contact Arandia Law Firm | Schedule a Consultation",
      description:
        "Speak with our trilingual immigration team — offices in NY, NJ and FL, serving clients nationwide by video. Criminal defense in New York.",
    },
    PT: {
      title: "Fale com a Arandia Law Firm | Agende sua Avaliação",
      description:
        "Fale com nossa equipe trilíngue de imigração. Escritórios em NY, NJ e FL, atendendo todo o país por vídeo. Defesa criminal em Nova York.",
    },
    ES: {
      title: "Contacte a Arandia Law Firm | Agende su Evaluación",
      description:
        "Hable con nuestro equipo trilingüe de inmigración. Oficinas en NY, NJ y FL, atendiendo todo el país por video. Defensa penal en Nueva York.",
    },
  },
  "/practice-areas": {
    EN: {
      title: "Practice Areas | Immigration & Defense | Arandia Law",
      description:
        "Deportation defense, family immigration, green cards, citizenship, waivers, EB-2 NIW, and NY criminal defense — all under one roof.",
    },
    PT: {
      title: "Áreas de Atuação | Imigração e Defesa Criminal",
      description:
        "Defesa contra deportação, imigração familiar, green cards, cidadania, waivers, EB-2 NIW e defesa criminal em Nova York — tudo no mesmo lugar.",
    },
    ES: {
      title: "Áreas de Práctica | Inmigración y Defensa Penal",
      description:
        "Defensa contra deportación, inmigración familiar, green cards, ciudadanía, waivers, EB-2 NIW y defensa penal en Nueva York — todo en un solo lugar.",
    },
  },
  "/services/deportation": {
    EN: {
      title: "Deportation Defense Attorneys | NY, NJ & FL | Arandia Law",
      description:
        "Aggressive removal defense in immigration court. Master calendar, individual hearings, bond, cancellation of removal, and appeals.",
    },
    PT: {
      title: "Advogado de Defesa contra Deportação | NY, NJ e FL",
      description:
        "Defesa agressiva em corte de imigração. Master calendar, audiências individuais, fiança, cancelamento de remoção e recursos.",
    },
    ES: {
      title: "Abogado de Defensa contra Deportación | NY, NJ y FL",
      description:
        "Defensa agresiva en corte de inmigración. Master calendar, audiencias individuales, fianza, cancelación de remoción y apelaciones.",
    },
  },
  "/services/crimmigration": {
    EN: {
      title: "Crimmigration Lawyer | Criminal Charges + Immigration",
      description:
        "Criminal charges can end your immigration case. We handle New York criminal defense alongside immigration practice in New York, New Jersey and Florida.",
    },
    PT: {
      title: "Advogado de Crimmigration | Crime e Imigração",
      description:
        "Uma acusação criminal pode encerrar seu caso de imigração. Fazemos defesa criminal em Nova York junto com imigração em NY, Nova Jersey e Flórida.",
    },
    ES: {
      title: "Abogado de Crimmigration | Delitos e Inmigración",
      description:
        "Un cargo penal puede terminar su caso migratorio. Hacemos defensa penal en Nueva York junto con inmigración en NY, Nueva Jersey y Florida.",
    },
  },
  "/services/family-immigration": {
    EN: {
      title: "Family Immigration Lawyers | Petitions & Visas",
      description:
        "Reunite your family with I-130 petitions, K-1 fiancé visas, marriage-based green cards, and adjustment of status filings.",
    },
    PT: {
      title: "Imigração Familiar | Petições e Vistos | Arandia Law",
      description:
        "Reúna sua família com petições I-130, vistos de noivo K-1, green card por casamento e ajuste de status.",
    },
    ES: {
      title: "Inmigración Familiar | Peticiones y Visas",
      description:
        "Reúna a su familia con peticiones I-130, visas de prometido K-1, green card por matrimonio y ajuste de estatus.",
    },
  },
  "/services/green-cards": {
    EN: {
      title: "Green Card Attorneys | Permanent Residency | Arandia Law",
      description:
        "Marriage, family, and employment-based green cards. Adjustment of status and consular processing handled end-to-end.",
    },
    PT: {
      title: "Advogado de Green Card | Residência Permanente",
      description:
        "Green card por casamento, família e emprego. Ajuste de status e processamento consular conduzidos do início ao fim.",
    },
    ES: {
      title: "Abogado de Green Card | Residencia Permanente",
      description:
        "Green card por matrimonio, familia y empleo. Ajuste de estatus y procesamiento consular gestionados de principio a fin.",
    },
  },
  "/services/citizenship": {
    EN: {
      title: "U.S. Citizenship & Naturalization Lawyers | Arandia Law",
      description:
        "N-400 naturalization, eligibility analysis, and citizenship interview prep. Take our free citizenship eligibility quiz.",
    },
    PT: {
      title: "Cidadania Americana e Naturalização | Arandia Law",
      description:
        "Naturalização N-400, análise de elegibilidade e preparação para a entrevista. Faça nosso teste gratuito de cidadania.",
    },
    ES: {
      title: "Ciudadanía Americana y Naturalización | Arandia Law",
      description:
        "Naturalización N-400, análisis de elegibilidad y preparación para la entrevista. Haga nuestro examen gratuito de ciudadanía.",
    },
  },
  "/citizenship-date-calculator": {
    EN: {
      title: "N-400 Early Filing Calculator: When Can I Apply?",
      description:
        "Enter your green card date to get the earliest day USCIS lets you file for citizenship — 90 days before your 5- or 3-year mark — and what else must be true.",
    },
    PT: {
      title: "Calculadora da Cidadania Americana: Quando Pedir?",
      description:
        "Informe a data do green card e veja o primeiro dia em que o USCIS aceita o pedido de cidadania — 90 dias antes dos 5 ou 3 anos — e o que mais é exigido.",
    },
    ES: {
      title: "Calculadora de Ciudadanía: ¿Cuándo Puedo Aplicar?",
      description:
        "Ingrese la fecha de su green card y vea el primer día en que USCIS acepta su solicitud de ciudadanía — 90 días antes de los 5 o 3 años — y qué más se exige.",
    },
  },
  "/citizenship-test": {
    EN: {
      title: "US Citizenship Practice Test 2026 | 128 USCIS Questions",
      description:
        "Free 2026 US citizenship practice test using the official 128-question USCIS civics bank. Answer 12 of 20 correctly to pass — practice now.",
    },
    PT: {
      title: "Teste de Cidadania Americana 2026 | 128 Perguntas",
      description:
        "Teste prático de cidadania americana 2026 com as 128 perguntas oficiais do USCIS. Acerte 12 de 20 para passar. Gratuito, comece agora.",
    },
    ES: {
      title: "Examen de Ciudadanía Americana 2026 | 128 Preguntas",
      description:
        "Examen de práctica de ciudadanía 2026 con las 128 preguntas oficiales de USCIS. Acierte 12 de 20 para aprobar. Gratis, empiece ahora.",
    },
  },
  "/services/dui-dwi-defense": {
    EN: {
      title: "DUI / DWI Defense Lawyers | New York | Arandia Law",
      description:
        "NY DWI and DUI defense for residents and non-citizens. Protect your license, record, and immigration status.",
    },
    PT: {
      title: "Advogado de DUI/DWI em Nova York | Arandia Law",
      description:
        "Defesa de DWI e DUI em Nova York para imigrantes e residentes. Proteja sua carteira, sua ficha criminal e seu status de imigração.",
    },
    ES: {
      title: "Abogado de DUI/DWI en Nueva York | Arandia Law",
      description:
        "Defensa de DWI y DUI en Nueva York para inmigrantes y residentes. Proteja su licencia, su récord y su estatus migratorio.",
    },
  },
  "/services/drug-defense": {
    EN: {
      title: "Drug Charge Defense | New York | Arandia Law",
      description:
        "Aggressive defense against possession, distribution, and trafficking charges in NY — with full awareness of immigration consequences.",
    },
    PT: {
      title: "Defesa Criminal de Drogas em Nova York | Arandia",
      description:
        "Defesa contra acusações de posse, venda e tráfico em Nova York, com atenção total às consequências para o seu status de imigração.",
    },
    ES: {
      title: "Defensa Penal por Drogas en Nueva York | Arandia",
      description:
        "Defensa contra cargos de posesión, venta y tráfico en Nueva York, con plena atención a las consecuencias migratorias del caso.",
    },
  },
  "/services/domestic-violence": {
    EN: {
      title: "Domestic Violence Defense | New York | Arandia Law",
      description:
        "Strategic defense for domestic violence allegations in NY criminal court — protecting your record, family, and immigration status.",
    },
    PT: {
      title: "Defesa de Violência Doméstica em Nova York",
      description:
        "Defesa estratégica em acusações de violência doméstica na corte criminal de Nova York, protegendo sua ficha, sua família e sua imigração.",
    },
    ES: {
      title: "Defensa de Violencia Doméstica en Nueva York",
      description:
        "Defensa estratégica ante cargos de violencia doméstica en la corte penal de Nueva York, protegiendo su récord, familia y estatus.",
    },
  },
  "/services/waivers": {
    EN: {
      title: "Immigration Waivers (I-601, I-601A, I-212) | Arandia Law",
      description:
        "Hardship waivers for unlawful presence, prior removals, and inadmissibility. Reunite with your family in the U.S.",
    },
    PT: {
      title: "Perdões de Imigração I-601, I-601A e I-212",
      description:
        "Perdão por presença ilegal, deportação anterior e inadmissibilidade. Reúna sua família nos Estados Unidos. Atendimento em todo o país.",
    },
    ES: {
      title: "Perdones Migratorios I-601, I-601A y I-212 | Arandia",
      description:
        "Perdón por presencia ilegal, deportación previa e inadmisibilidad. Reúnase con su familia en Estados Unidos. Atendemos en todo el país.",
    },
  },
  // LP de trafego pago. Entra aqui so para o titulo da aba e para o canonical
  // nao cairem no fallback da home; a pagina e noindex via NOINDEX_PREFIXES.
  "/lp/queens": {
    EN: {
      title: "Immigration Lawyers in Queens, NY | Arandia Law Firm",
      description:
        "Tell us what you are facing and a lawyer from our Kew Gardens office will review it. English, Portuguese and Spanish, in person or by video.",
    },
    PT: {
      title: "Advogados de Imigração em Queens, NY | Arandia",
      description:
        "Conte o que está acontecendo e um advogado do escritório de Kew Gardens vai analisar. Português, espanhol e inglês, presencial ou por vídeo.",
    },
    ES: {
      title: "Abogados de Inmigración en Queens, NY | Arandia",
      description:
        "Cuéntenos qué está pasando y un abogado de la oficina de Kew Gardens lo revisará. Español, portugués e inglés, presencial o por video.",
    },
  },
  "/about": {
    EN: {
      title: "Alexander Arandia \u2014 Immigration Attorney | Arandia Law Firm",
      description:
        "New York Bar since 1984, Columbia Law. Immigration and criminal defense across NY, NJ and FL, and a recurring voice on Spanish-language television and radio.",
    },
    PT: {
      title: "Alexander Arandia \u2014 Advogado de Imigra\u00e7\u00e3o | Arandia Law Firm",
      description:
        "Ordem de Nova York desde 1984, Columbia Law. Imigra\u00e7\u00e3o e defesa criminal em NY, NJ e FL, com presen\u00e7a constante na TV e no r\u00e1dio em espanhol.",
    },
    ES: {
      title: "Alexander Arandia \u2014 Abogado de Inmigraci\u00f3n | Arandia Law Firm",
      description:
        "Colegio de Nueva York desde 1984, Columbia Law. Inmigraci\u00f3n y defensa penal en NY, NJ y FL, con presencia constante en la televisi\u00f3n y la radio en espa\u00f1ol.",
    },
  },
  "/services/asylum": {
    EN: {
      title: "Asylum Lawyer in Queens NY and New Jersey | Arandia",
      description:
        "Affirmative asylum at USCIS and defensive asylum in immigration court, including fraudulent filings made in your name. Queens, Newark, nationwide.",
    },
    PT: {
      title: "Advogado de Asilo em Queens NY e Nova Jersey",
      description:
        "Asilo afirmativo no USCIS e asilo defensivo na corte de imigração, inclusive pedido fraudulento aberto no seu nome. Queens, Newark e todo o país.",
    },
    ES: {
      title: "Abogado de Asilo en Queens NY y Nueva Jersey",
      description:
        "Asilo afirmativo ante USCIS y asilo defensivo en la corte, incluso solicitudes fraudulentas presentadas a su nombre. Queens, Newark y todo el país.",
    },
  },
  // Onda 4 (03/11/2026).
  "/services/ice-detention-bond-hearings": {
    EN: {
      title: "ICE Detention & Bond Hearing Lawyer | Arandia",
      description:
        "Detained by ICE? We find your relative, ask a judge for bond under the 2026 circuit rulings and build the release case. NY, NJ, FL and nationwide.",
    },
    PT: {
      title: "Advogado de Detenção do ICE e Fiança | Arandia",
      description:
        "Familiar detido pelo ICE? Localizamos, pedimos fiança ao juiz com base nas decisões de 2026 e montamos o pedido de soltura. NY, NJ, FL e todo o país.",
    },
    ES: {
      title: "Abogado de Detención de ICE y Fianzas | Arandia",
      description:
        "¿Familiar detenido por ICE? Lo ubicamos, pedimos fianza al juez según los fallos de 2026 y armamos la solicitud de libertad. NY, NJ, FL y todo el país.",
    },
  },
  "/services/u-visa-vawa": {
    EN: {
      title: "U Visa & VAWA Lawyer in Queens NY and New Jersey",
      description:
        "VAWA self-petitions under the December 2025 USCIS rules and U petitions for crime victims — including cases already in removal proceedings. Queens, West New York, nationwide.",
    },
    PT: {
      title: "Advogado de Visto U e VAWA em Queens NY e Nova Jersey",
      description:
        "Autopetições VAWA sob as regras do USCIS de dezembro de 2025 e petições U para vítimas de crime — inclusive em processo de deportação. Queens, West New York e todo o país.",
    },
    ES: {
      title: "Abogado de Visa U y VAWA en Queens NY y Nueva Jersey",
      description:
        "Autopeticiones VAWA bajo las reglas de USCIS de diciembre de 2025 y peticiones U para víctimas de delitos — incluso en proceso de deportación. Queens, West New York y todo el país.",
    },
  },
  "/services/business-talent-visas": {
    EN: {
      title: "Business & Talent Visas | E-2, O-1, L-1, EB-5 | Arandia",
      description:
        "Investor, executive, and extraordinary-ability visas for entrepreneurs and professionals relocating to the U.S.",
    },
    PT: {
      title: "Vistos de Negócios e Talento | E-2, O-1, L-1, EB-5",
      description:
        "Vistos para investidores, executivos e profissionais de habilidade extraordinária que vão abrir ou transferir negócios para os EUA.",
    },
    ES: {
      title: "Visas de Negocios y Talento | E-2, O-1, L-1, EB-5",
      description:
        "Visas para inversionistas, ejecutivos y profesionales de habilidad extraordinaria que abren o trasladan su negocio a Estados Unidos.",
    },
  },
  "/services/eb2-niw-visa": {
    EN: {
      title: "EB-2 NIW Green Card | National Interest Waiver | Arandia",
      description:
        "Self-petition for permanent residency without a job offer. Strategy and filing for STEM, healthcare, and tech professionals.",
    },
    PT: {
      title: "Green Card EB-2 NIW | Interesse Nacional | Arandia",
      description:
        "Autopetição de residência permanente sem oferta de emprego. Estratégia e protocolo para profissionais de STEM, saúde e tecnologia.",
    },
    ES: {
      title: "Green Card EB-2 NIW | Interés Nacional | Arandia Law",
      description:
        "Autopetición de residencia permanente sin oferta de trabajo. Estrategia y presentación para profesionales de STEM, salud y tecnología.",
    },
  },
  "/services/o1-visa": {
    EN: {
      title: "O-1 Visa Lawyer | Founders, Researchers & Artists",
      description:
        "No lottery, no cap, no employer sponsor required. We build O-1 petitions for founders, researchers, artists and athletes. Nationwide, by video.",
    },
    PT: {
      title: "Advogado de Visto O-1 | Fundadores e Pesquisadores",
      description:
        "Sem sorteio, sem limite anual e sem empregador patrocinador. Montamos petições O-1 para fundadores, pesquisadores, artistas e atletas.",
    },
    ES: {
      title: "Abogado de Visa O-1 | Fundadores e Investigadores",
      description:
        "Sin sorteo, sin límite anual y sin empleador patrocinador. Armamos peticiones O-1 para fundadores, investigadores, artistas y atletas.",
    },
  },
  "/services/e2-investor-visa": {
    EN: {
      title: "E-2 Investor Visa Lawyer | Boca Raton & New York",
      description:
        "Invest in and run a US business on an E-2 visa. Treaty eligibility, business plan, source of funds and interview prep. Boca Raton and New York.",
    },
    PT: {
      title: "Visto de Investidor E-2 | Boca Raton e Nova York",
      description:
        "Invista e administre um negócio nos EUA com o visto E-2. Elegibilidade por tratado, plano de negócios, origem dos fundos e entrevista.",
    },
    ES: {
      title: "Visa de Inversionista E-2 | Boca Raton y Nueva York",
      description:
        "Invierta y opere un negocio en EE. UU. con la visa E-2. Elegibilidad por tratado, plan de negocios, origen de fondos y entrevista.",
    },
  },
  "/insights": {
    EN: {
      title: "Insights & Legal Guides | Arandia Law Firm",
      description:
        "Trilingual articles on U.S. immigration, removal defense, and criminal law — written by attorneys for the people we serve.",
    },
    PT: {
      title: "Artigos e Guias Jurídicos | Arandia Law Firm",
      description:
        "Artigos trilíngues sobre imigração americana, defesa contra deportação e direito criminal, escritos por advogados para quem precisa.",
    },
    ES: {
      title: "Artículos y Guías Legales | Arandia Law Firm",
      description:
        "Artículos trilingües sobre inmigración estadounidense, defensa contra deportación y derecho penal, escritos por abogados para usted.",
    },
  },
  "/terms-and-conditions": {
    EN: {
      title: "Terms & Conditions | Arandia Law Firm",
      description: "Terms governing use of the Arandia Law Firm website.",
    },
    PT: {
      title: "Termos e Condições | Arandia Law Firm",
      description: "Termos que regem o uso do site da Arandia Law Firm.",
    },
    ES: {
      title: "Términos y Condiciones | Arandia Law Firm",
      description: "Términos que rigen el uso del sitio web de Arandia Law Firm.",
    },
  },
  "/privacy-policy": {
    EN: {
      title: "Privacy Policy | Arandia Law Firm",
      description:
        "How Arandia Law Firm collects, uses, and protects your information.",
    },
    PT: {
      title: "Política de Privacidade | Arandia Law Firm",
      description:
        "Como a Arandia Law Firm coleta, usa e protege as suas informações.",
    },
    ES: {
      title: "Política de Privacidad | Arandia Law Firm",
      description:
        "Cómo Arandia Law Firm recopila, usa y protege su información.",
    },
  },
  // Titulos reescritos em 19/10/2026: as buscas sao por "Queens", nao por
  // "Kew Gardens", e o espanhol e o buraco — "abogado de inmigracion queens ny"
  // tem 65 impressoes em posicao 49,2 enquanto o ingles de Queens ja esta em ~9.
  "/locations/new-york": {
    EN: {
      title: "Immigration Lawyer in Queens, NY | Kew Gardens Office",
      description:
        "Immigration and New York criminal defense from our Kew Gardens office. Forest Hills, Jackson Heights, Elmhurst, Astoria and all of Queens.",
    },
    PT: {
      title: "Advogado de Imigração em Queens, NY | Kew Gardens",
      description:
        "Imigração e defesa criminal em Nova York, do escritório de Kew Gardens. Forest Hills, Jackson Heights, Elmhurst, Astoria e todo o Queens.",
    },
    ES: {
      title: "Abogado de Inmigración en Queens, NY | Kew Gardens",
      description:
        "Inmigración y defensa penal en Nueva York, desde nuestra oficina de Kew Gardens. Forest Hills, Jackson Heights, Elmhurst, Astoria y todo Queens.",
    },
  },
  // Reescritos em 19/10/2026: as buscas sao por "new jersey" e por "newark",
  // nao por "west new york", que e onde o escritorio fica mas ninguem procura.
  // O espanhol no plural casa com "abogados de inmigracion en nj" (70 imp) e
  // "abogados de inmigracion en new jersey" (36 imp).
  /*
    Title e description mirando os termos que o condado realmente busca:
    green card, naturalizacao/cidadania e imigracao familiar e empresarial.
    A /locations/new-jersey mira "Newark & Hudson", que quase nao aparece nas
    buscas — por isso as duas nao competem.
  */
  "/locations/bergen-county": {
    EN: {
      title: "Immigration Lawyer for Bergen County, NJ | Arandia",
      description:
        "Green cards, naturalization, family petitions and business immigration for Bergen County — Hackensack, Fort Lee, Englewood, Teaneck and Paramus. Served from West New York.",
    },
    PT: {
      title: "Advogado de Imigração para Bergen County, NJ",
      description:
        "Green card, naturalização, petições familiares e imigração empresarial para Bergen County — Hackensack, Fort Lee, Englewood, Teaneck e Paramus. Atendimento a partir de West New York.",
    },
    ES: {
      title: "Abogado de Inmigración para Bergen County, NJ",
      description:
        "Green cards, naturalización, peticiones familiares e inmigración de negocios para Bergen County — Hackensack, Fort Lee, Englewood, Teaneck y Paramus. Desde West New York.",
    },
  },
  "/locations/new-jersey": {
    EN: {
      title: "Immigration Lawyer in New Jersey | Hudson County",
      description:
        "Family petitions, green cards, citizenship and removal defense across New Jersey from our West New York office: Union City, Hoboken, Newark and Hudson County.",
    },
    PT: {
      title: "Advogado de Imigração em Nova Jersey | Hudson County",
      description:
        "Petições familiares, green card, cidadania e defesa contra deportação em Nova Jersey, do escritório de West New York: Union City, Hoboken, Newark e Hudson County.",
    },
    ES: {
      title: "Abogados de Inmigración en New Jersey | Hudson County",
      description:
        "Peticiones familiares, green card, ciudadanía y defensa contra la deportación en Nueva Jersey, desde West New York: Union City, Hoboken, Newark y Hudson County.",
    },
  },
  /*
    Onda 1 de paginas de bairro (30/10/2026). Title e H1 dizem "for/para",
    nao "in/em": nenhuma delas tem escritorio no lugar do titulo.
  */
  "/locations/forest-hills": {
    EN: {
      title: "Immigration Lawyer for Forest Hills, Queens | Arandia",
      description:
        "Asylum, deportation defense and NY criminal cases with immigration stakes for Forest Hills, from our Kew Gardens office next door. Video consults nationwide.",
    },
    PT: {
      title: "Advogado de Imigração para Forest Hills, Queens",
      description:
        "Asilo, defesa contra deportação e casos criminais em NY com risco imigratório para Forest Hills, do escritório vizinho em Kew Gardens. Consulta por vídeo.",
    },
    ES: {
      title: "Abogado de Inmigración para Forest Hills, Queens",
      description:
        "Asilo, defensa contra la deportación y casos penales en NY con riesgo migratorio para Forest Hills, desde nuestra oficina vecina en Kew Gardens.",
    },
  },
  "/locations/jackson-heights": {
    EN: {
      title: "Immigration Lawyer for Jackson Heights, Queens | Arandia",
      description:
        "Family petitions, green cards, citizenship and deportation defense for Jackson Heights in Spanish, Portuguese and English. Direct E/F train to Kew Gardens.",
    },
    PT: {
      title: "Advogado de Imigração para Jackson Heights, Queens",
      description:
        "Petições familiares, green card, cidadania e defesa contra deportação para Jackson Heights, em português e espanhol. Trem E/F direto até Kew Gardens.",
    },
    ES: {
      title: "Abogado de Inmigración para Jackson Heights, Queens",
      description:
        "Peticiones familiares, green card, ciudadanía y defensa contra la deportación para Jackson Heights, en español. Tren E/F directo a Kew Gardens.",
    },
  },
  "/locations/union-city": {
    EN: {
      title: "Immigration Lawyer for Union City, NJ | Arandia",
      description:
        "Family petitions, green cards, citizenship and deportation defense for Union City, from our West New York office next door. Spanish, Portuguese and English.",
    },
    PT: {
      title: "Advogado de Imigração para Union City, NJ",
      description:
        "Petições familiares, green card, cidadania e defesa contra deportação para Union City, do escritório vizinho em West New York. Em português e espanhol.",
    },
    ES: {
      title: "Abogado de Inmigración para Union City, NJ",
      description:
        "Peticiones familiares, green card, ciudadanía y defensa contra la deportación para Union City y la zona de Bergenline, desde nuestra oficina vecina en West New York.",
    },
  },
  "/locations/hoboken": {
    EN: {
      title: "Immigration Lawyer for Hoboken, NJ | Arandia",
      description:
        "EB-2 NIW, O-1, H-1B, marriage green cards and citizenship for Hoboken residents, from our West New York office in Hudson County. Video consults available.",
    },
    PT: {
      title: "Advogado de Imigração para Hoboken, NJ",
      description:
        "EB-2 NIW, O-1, H-1B, green card por casamento e cidadania para moradores de Hoboken, do escritório de West New York, em Hudson County.",
    },
    ES: {
      title: "Abogado de Inmigración para Hoboken, NJ",
      description:
        "EB-2 NIW, O-1, H-1B, green card por matrimonio y ciudadanía para residentes de Hoboken, desde nuestra oficina de West New York, en Hudson County.",
    },
  },
  "/locations/newark": {
    EN: {
      title: "Immigration Lawyer for Newark, NJ | Arandia",
      description:
        "Deportation defense at the Newark Immigration Court (970 Broad St), ICE detention and bond, asylum and family cases. From our West New York office.",
    },
    PT: {
      title: "Advogado de Imigração para Newark, NJ",
      description:
        "Defesa contra deportação na Corte de Imigração de Newark (970 Broad St), detenção do ICE e fiança, asilo e casos familiares. Escritório em West New York.",
    },
    ES: {
      title: "Abogado de Inmigración para Newark, NJ",
      description:
        "Defensa contra la deportación en la Corte de Inmigración de Newark (970 Broad St), detención de ICE y fianza, asilo y casos familiares.",
    },
  },
  // Onda 2 de paginas de bairro (30/10/2026).
  "/locations/elizabeth": {
    EN: {
      title: "Immigration Lawyer for Elizabeth, NJ | Arandia",
      description:
        "Detention, bond and deportation defense at the Elizabeth Immigration Court (625 Evans St), plus family immigration. From our West New York office.",
    },
    PT: {
      title: "Advogado de Imigração para Elizabeth, NJ",
      description:
        "Detenção, fiança e defesa contra deportação na Corte de Imigração de Elizabeth (625 Evans St), além de imigração familiar. Escritório em West New York.",
    },
    ES: {
      title: "Abogado de Inmigración para Elizabeth, NJ",
      description:
        "Detención, fianza y defensa contra la deportación en la Corte de Inmigración de Elizabeth (625 Evans St), e inmigración familiar. Desde West New York.",
    },
  },
  "/locations/brooklyn": {
    EN: {
      title: "Immigration & Criminal Lawyer for Brooklyn, NY | Arandia",
      description:
        "Criminal defense for immigrants in Brooklyn, with the immigration consequence read before any plea, plus deportation defense. Direct F train to Kew Gardens.",
    },
    PT: {
      title: "Advogado de Imigração e Criminal para o Brooklyn",
      description:
        "Defesa criminal para imigrantes no Brooklyn, com a consequência imigratória lida antes de qualquer acordo, e defesa contra deportação. Trem F direto.",
    },
    ES: {
      title: "Abogado de Inmigración y Penal para Brooklyn, NY",
      description:
        "Defensa penal para inmigrantes en Brooklyn, con la consecuencia migratoria revisada antes de cualquier acuerdo, y defensa contra la deportación.",
    },
  },
  "/locations/bronx": {
    EN: {
      title: "Immigration & Criminal Lawyer for the Bronx | Arandia",
      description:
        "DWI and criminal defense with the immigration consequences in view, deportation defense and family immigration for the Bronx. Spanish, Portuguese, English.",
    },
    PT: {
      title: "Advogado de Imigração e Criminal para o Bronx",
      description:
        "Defesa em DWI e casos criminais com a consequência imigratória à vista, defesa contra deportação e imigração familiar para o Bronx. Em português e espanhol.",
    },
    ES: {
      title: "Abogado de Inmigración y Penal para el Bronx, NY",
      description:
        "Defensa en DWI y casos penales con la consecuencia migratoria a la vista, defensa contra la deportación e inmigración familiar para el Bronx. En español.",
    },
  },
  "/locations/manhattan": {
    EN: {
      title: "Immigration Lawyer for Manhattan, NY | Arandia",
      description:
        "All three NYC immigration courts are in Manhattan: 26 Federal Plaza, 290 Broadway and Varick. Deportation defense, asylum and appeals, from Kew Gardens.",
    },
    PT: {
      title: "Advogado de Imigração para Manhattan, NY",
      description:
        "As três cortes de imigração de Nova York ficam em Manhattan: 26 Federal Plaza, 290 Broadway e Varick. Defesa contra deportação, asilo e recursos.",
    },
    ES: {
      title: "Abogado de Inmigración para Manhattan, NY",
      description:
        "Las tres cortes de inmigración de Nueva York están en Manhattan: 26 Federal Plaza, 290 Broadway y Varick. Defensa contra la deportación, asilo y apelaciones.",
    },
  },
  "/locations/long-island": {
    EN: {
      title: "Immigration & Criminal Lawyer for Long Island | Arandia",
      description:
        "Immigration cases and New York criminal defense for Nassau and Suffolk: DWI, plea review and deportation defense. Spanish, Portuguese and English.",
    },
    PT: {
      title: "Advogado de Imigração e Criminal para Long Island",
      description:
        "Imigração e defesa criminal em Nova York para Nassau e Suffolk: DWI, revisão de acordos e defesa contra deportação. Em português e espanhol.",
    },
    ES: {
      title: "Abogados de Inmigración para Long Island, NY",
      description:
        "Inmigración y defensa penal en Nueva York para Nassau y Suffolk: DWI, revisión de acuerdos y defensa contra la deportación. En español y portugués.",
    },
  },
  // Onda 3 de paginas de area (02/11/2026).
  "/locations/north-bergen": {
    EN: {
      title: "Immigration Lawyer for North Bergen, NJ | Arandia",
      description:
        "245(i) green cards, cancellation of removal and family petitions for North Bergen, from our West New York office next door. Spanish, Portuguese, English.",
    },
    PT: {
      title: "Advogado de Imigração para North Bergen, NJ",
      description:
        "Green card pela 245(i), cancelamento de remoção e petições familiares para North Bergen, do nosso escritório vizinho em West New York. Em português.",
    },
    ES: {
      title: "Abogado de Inmigración para North Bergen, NJ",
      description:
        "Green cards por la 245(i), cancelación de remoción y peticiones familiares para North Bergen, desde nuestra oficina vecina en West New York. En español.",
    },
  },
  "/locations/paterson": {
    EN: {
      title: "Immigration Lawyer for Paterson, NJ | Arandia",
      description:
        "Family petitions, green cards, citizenship and deportation defense for Paterson and Passaic County. Newark court hearings. Spanish, Portuguese, English.",
    },
    PT: {
      title: "Advogado de Imigração para Paterson, NJ",
      description:
        "Petições familiares, green card, cidadania e defesa contra deportação para Paterson e o condado de Passaic. Audiências em Newark. Em português.",
    },
    ES: {
      title: "Abogado de Inmigración para Paterson, NJ",
      description:
        "Peticiones familiares, green cards, ciudadanía y defensa contra la deportación para Paterson y el condado de Passaic. Audiencias en Newark. En español.",
    },
  },
  "/locations/hackensack": {
    EN: {
      title: "Business Immigration Lawyer for Hackensack, NJ",
      description:
        "EB-2 NIW, O-1 and H-1B petitions for professionals and employers in Hackensack and Bergen County, plus family green cards and citizenship.",
    },
    PT: {
      title: "Advogado de Imigração Empresarial para Hackensack",
      description:
        "EB-2 NIW, O-1 e H-1B para profissionais e empresas de Hackensack e do condado de Bergen, além de green card por família e cidadania. Em português.",
    },
    ES: {
      title: "Abogado de Inmigración de Negocios para Hackensack",
      description:
        "EB-2 NIW, O-1 y H-1B para profesionales y empresas de Hackensack y el condado de Bergen, además de green cards por familia y ciudadanía. En español.",
    },
  },
  "/locations/atlantic-city": {
    EN: {
      title: "Immigration Lawyer for Atlantic City, NJ | Arandia",
      description:
        "Work permit renewals, family green cards, asylum and deportation defense for Atlantic City and Atlantic County, by WhatsApp video and in court.",
    },
    PT: {
      title: "Advogado de Imigração para Atlantic City, NJ",
      description:
        "Renovação de autorização de trabalho, green card por família, asilo e defesa contra deportação para Atlantic City e o condado de Atlantic.",
    },
    ES: {
      title: "Abogado de Inmigración para Atlantic City, NJ",
      description:
        "Renovación de permisos de trabajo, green cards por familia, asilo y defensa contra la deportación para Atlantic City y el condado de Atlantic.",
    },
  },
  "/locations/broward-county": {
    EN: {
      title: "Immigration Lawyer for Broward County, FL | Arandia",
      description:
        "Marriage green cards, business visas, asylum and deportation defense for Broward County, from our Boca Raton office. Portuguese, Spanish, English.",
    },
    PT: {
      title: "Advogado de Imigração para o Condado de Broward",
      description:
        "Green card por casamento, vistos de negócios, asilo e defesa contra deportação para Broward, do nosso escritório em Boca Raton. Em português.",
    },
    ES: {
      title: "Abogado de Inmigración para el Condado de Broward",
      description:
        "Green cards por matrimonio, visas de negocios, asilo y defensa contra la deportación para Broward, desde nuestra oficina de Boca Raton. En español.",
    },
  },
  "/locations/florida": {
    EN: {
      title: "Immigration Lawyer in Boca Raton, FL | Arandia",
      description:
        "Arandia Law Firm's Florida office in Boca Raton. Business visas, investor immigration and talent petitions for Palm Beach County.",
    },
    PT: {
      title: "Advogado de Imigração em Boca Raton, FL",
      description:
        "Escritório da Arandia Law Firm em Boca Raton, FL. Vistos empresariais, imigração de investidores e petições de talento em Palm Beach.",
    },
    ES: {
      title: "Abogado de Inmigración en Boca Raton, FL",
      description:
        "Oficina de Arandia Law Firm en Boca Raton, FL. Visas de negocios, inmigración de inversionistas y peticiones de talento en Palm Beach.",
    },
  },
};

/**
 * Only reached by an article route that has no entry of its own. Each article
 * page layers its own title and description on top, so this is the safety net,
 * not the normal path. It still has to be translated: an untranslated fallback
 * means a PT reader gets an English snippet in the SERP.
 */
const INSIGHTS_FALLBACK: PageMeta = {
  EN: {
    title: "Legal Insight | Arandia Law Firm",
    description:
      "In-depth article on U.S. immigration or criminal defense from the attorneys at Arandia Law Firm.",
  },
  PT: {
    title: "Artigo Jurídico | Arandia Law Firm",
    description:
      "Artigo detalhado sobre imigração americana ou defesa criminal, escrito pelos advogados da Arandia Law Firm.",
  },
  ES: {
    title: "Artículo Legal | Arandia Law Firm",
    description:
      "Artículo detallado sobre inmigración estadounidense o defensa penal, escrito por los abogados de Arandia Law Firm.",
  },
};

const ALIAS_TO_CANONICAL: Record<string, string> = {
  "/services/dui-dwi": "/services/dui-dwi-defense",
  "/services/drug-charges": "/services/drug-defense",
};

const normalizePath = (path: string): string => ALIAS_TO_CANONICAL[path] ?? path;

/**
 * Landing page de trafego pago existe para converter clique de anuncio, nao
 * para rankear, e se for indexada passa a competir com /locations/new-york
 * pelos mesmos termos de Queens. Rota com esse prefixo tambem fica fora do
 * generate-sitemap.cjs.
 *
 * A lista mora em @/lib/seo porque o StickyActionBar precisa da mesma resposta
 * para esconder o link de saida nessas paginas. isNoIndexPath e mais amplo que
 * isPaidLandingPath: inclui /web, que e so redirect e nao deve ser rastreado.
 */
const robotsFor = (cleanPath: string) =>
  isNoIndexPath(cleanPath) ? "noindex, nofollow" : "index, follow";

const OG_LOCALE: Record<Locale, string> = {
  en: "en_US",
  pt: "pt_BR",
  es: "es_LA",
};

const localeKey = (locale: Locale): "EN" | "PT" | "ES" =>
  locale === "pt" ? "PT" : locale === "es" ? "ES" : "EN";

const resolveMeta = (cleanPath: string): PageMeta => {
  if (PAGE_META[cleanPath]) return PAGE_META[cleanPath];
  // Insights articles share a default; their pages may layer on more specific tags.
  if (cleanPath.startsWith("/insights/")) return INSIGHTS_FALLBACK;
  return PAGE_META["/"];
};

const SEOHead = () => {
  const { pathname } = useLocation();
  const cleanPath = normalizePath(stripLocalePrefix(pathname));
  const locale = getLocaleFromPath(pathname);
  const pageMeta = resolveMeta(cleanPath);
  const meta = pageMeta[localeKey(locale)] ?? pageMeta.EN;
  const canonical = buildUrl(locale, cleanPath);
  const ogImage = pageMeta.image ?? DEFAULT_IMAGE;
  const ogType = cleanPath.startsWith("/insights/") ? "article" : "website";

  return (
    <Helmet>
      <html lang={locale} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {/*
        robots passou a sair daqui para que a LP de trafego pago consiga ficar
        noindex. A tag estatica do index.html recebeu data-rh na mesma mudanca:
        sem isso o Helmet somaria uma segunda robots em vez de substituir, que e
        exatamente o problema que o item 7 resolveu. E ela precisa ser emitida
        em TODA pagina, nao so na LP — marcada com data-rh e ausente do conjunto
        novo, updateTags() apagaria a estatica no resto do site.
      */}
      <meta name="robots" content={robotsFor(cleanPath)} />
      <link rel="canonical" href={canonical} />

      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />

      {/*
        og:locale vinha só do index.html, fixo em en_US, e o SEOHead não emitia
        nenhum — então toda página /pt e /es se declarava em inglês para o
        Facebook e o WhatsApp. Agora sai daqui, por idioma, e a estática do
        index.html está marcada com data-rh para ser substituída e não somada.
        es_LA em vez de es_US porque es_US não está na lista de locales que o
        Facebook aceita; o público é hispanofalante latino-americano nos EUA.
      */}
      <meta property="og:locale" content={OG_LOCALE[locale]} />
      {LOCALES.filter((loc) => loc !== locale).map((loc) => (
        <meta key={loc} property="og:locale:alternate" content={OG_LOCALE[loc]} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={ogImage} />

      {LOCALES.map((loc) => (
        <link key={loc} rel="alternate" hrefLang={loc} href={buildUrl(loc, cleanPath)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildUrl("en", cleanPath)} />
    </Helmet>
  );
};

export default SEOHead;
